/**
 * Sezione "Evidenza prodotto" del dialogo Good360.
 *
 * Un Good360 può avere più evidenze (Nuovo Lancio / Homepage / Carosello / Widget), ognuna con
 * una propria finestra temporale. Qui si elencano, si creano, si modificano e si eliminano
 * (good360EvidenceCreate / good360EvidencePatch / nodeDelete).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Table, Button } from 'reactstrap';
import { EntityEditFetcher } from './EntityEditFetcher';
import { InlineEditForm } from './CompanyData360EditView';
import { Spinner } from './MainContentSpinner';
import { graphqlQuery } from '../utils/graphql';
import { client } from '../utils/client';
import { t } from '../utils/labels';
import { ENTITY } from '../utils/entities';
import { Good360EvidencesQuery } from '../graphql/query/Good360EvidencesQuery';
import { Good360EvidenceEditQuery } from '../graphql/query/Good360EvidenceEditQuery';
import { Good360EvidenceCreateMutation } from '../graphql/mutations/Good360EvidenceCreateMutation';
import { Good360EvidencePatchMutation } from '../graphql/mutations/Good360EvidencePatchMutation';
import { deleteNodeMutation } from '../graphql/mutations/DeleteNodeMutation';
import Good360EvidenceIntro from '../static/introspection/Good360Evidence.json';
import Good360EvidenceCreateIntro from '../static/introspection/Good360EvidenceCreateInput.json';
import Good360EvidencePatchIntro from '../static/introspection/Good360EvidencePatchInput.json';

const EVIDENCE_FIELDS = ['type', 'startDate', 'endDate'];

const fmtDate = (d: any): string => {
	if (!d) return '';
	const date = new Date(d);
	return isNaN(date.getTime()) ? '' : date.toLocaleDateString('it-IT');
};

type Mode = { kind: 'list' } | { kind: 'create' } | { kind: 'edit'; id: string };

export const Good360EvidencesSection: React.FC<{
	good360Id: string;
	onSaved: () => void;
}> = ({ good360Id, onSaved }) => {
	const [evidences, setEvidences] = useState<any[]>([]);
	const [loading, setLoading] = useState(true);
	const [busy, setBusy] = useState(false);
	const [mode, setMode] = useState<Mode>({ kind: 'list' });

	const backToList = () => setMode({ kind: 'list' });

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const r = await client.query<any, any>({
				query: Good360EvidencesQuery,
				variables: { id: good360Id },
				fetchPolicy: 'no-cache',
			});
			setEvidences((r.data && r.data.entity && r.data.entity.evidences) || []);
		} catch (e) {
			setEvidences([]);
		} finally {
			setLoading(false);
		}
	}, [good360Id]);

	useEffect(() => {
		load();
	}, [load]);

	const remove = async (id: string) => {
		if (!window.confirm(t`Good360/evidence/deleteConfirm`)) return;
		setBusy(true);
		try {
			await client.mutate({ mutation: deleteNodeMutation, variables: { id } });
			await load();
			onSaved();
		} finally {
			setBusy(false);
		}
	};

	if (loading) return <Spinner show={true} />;

	if (mode.kind === 'create') {
		return (
			<EntityEditFetcher
				create={true}
				modal={true}
				fetcher={null}
				mutation={Good360EvidenceCreateMutation}
				entityName={ENTITY.GOOD_360_EVIDENCE}
				typeIntro={Good360EvidenceIntro}
				inputTypeIntro={Good360EvidenceCreateIntro}
				fields={EVIDENCE_FIELDS}
				beforeSaveTransformer={(values: any) => ({
					...values,
					Good360: { id: good360Id },
				})}
				onSubmit={async () => {
					backToList();
					await load();
					onSaved();
				}}
			>
				{(args: any) => (
					<InlineEditForm
						{...args}
						subForm={{ kind: 'create', cancel: backToList }}
					/>
				)}
			</EntityEditFetcher>
		);
	}

	if (mode.kind === 'edit') {
		return (
			<EntityEditFetcher
				modal={true}
				fetcher={graphqlQuery(Good360EvidenceEditQuery, { id: mode.id })}
				id={mode.id}
				mutation={Good360EvidencePatchMutation}
				entityName={ENTITY.GOOD_360_EVIDENCE}
				typeIntro={Good360EvidenceIntro}
				inputTypeIntro={Good360EvidencePatchIntro}
				fields={EVIDENCE_FIELDS}
				onSubmit={async () => {
					backToList();
					await load();
					onSaved();
				}}
			>
				{(args: any) => (
					<InlineEditForm
						{...args}
						subForm={{ kind: 'edit', cancel: backToList }}
					/>
				)}
			</EntityEditFetcher>
		);
	}

	return (
		<div>
			<div
				style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}
			>
				<Button
					color="success"
					disabled={busy}
					onClick={() => setMode({ kind: 'create' })}
				>
					<i className="fa fa-plus" /> {t`create new`}
				</Button>
			</div>

			{evidences.length === 0 ? (
				<p className="text-muted">{t`Good360/evidence/none`}</p>
			) : (
				<Table hover>
					<thead>
						<tr>
							<th>{t`Good360Evidence/type/label`}</th>
							<th>{t`Good360Evidence/startDate/label`}</th>
							<th>{t`Good360Evidence/endDate/label`}</th>
							<th style={{ width: 160 }} />
						</tr>
					</thead>
					<tbody>
						{evidences.map((ev) => (
							<tr key={ev.id}>
								<td>{t(['Good360EvidenceType', ev.type, 'label'].join('/'))}</td>
								<td>{fmtDate(ev.startDate)}</td>
								<td>{fmtDate(ev.endDate)}</td>
								<td className="text-right">
									<Button
										size="sm"
										color="success"
										className="margin-right"
										disabled={busy}
										onClick={() => setMode({ kind: 'edit', id: ev.id })}
									>
										<i className="fa fa-pencil" />
									</Button>
									<Button
										size="sm"
										color="danger"
										disabled={busy}
										onClick={() => remove(ev.id)}
									>
										<i className="fa fa-trash" />
									</Button>
								</td>
							</tr>
						))}
					</tbody>
				</Table>
			)}
		</div>
	);
};

export const evidencesSummary = (evidences: any[] | undefined): string => {
	if (!evidences || evidences.length === 0) return '—';
	return evidences
		.map((ev) => t(['Good360EvidenceType', ev.type, 'label'].join('/')))
		.join(', ');
};
